// What Reseller needs from the app that hosts it (#216), handed over once at
// bootstrap — the same pattern as core's registerHttpClients and
// datacentral-react's configureDataHooks. The host calls createResellerModule
// from src/app/register-reseller.tsx; nothing here imports the app.
//
// Reseller opens no modals and reads no session, so unlike Dayoffs and
// Business it does not extend the shell's HostModalApi / HostSession. Every
// member is typed structurally, so the host's real objects are checked where it
// registers them, not here.
import type { ComponentType } from 'react'
import type { Control } from 'react-hook-form'
import type { HostLinks } from '@vnetwork/datacentral-shell/host'
import vi from './locales/vi.json'
import en from './locales/en.json'

/** The props Reseller gives the host's customer select (add-customer dialog). */
export interface ResellerCustomerSelectProps {
    control: Control<any>
    name: string
    label?: string
}

export interface ResellerHost {
    /**
     * The app's i18next instance, already initialised. The `reseller`
     * namespace is registered against it here. A package must not import
     * i18next itself to add bundles: that could hit an instance before the
     * app's init (#59 hazard 5).
     */
    i18n: {
        addResourceBundle: (
            lng: string,
            ns: string,
            resources: object
        ) => unknown
    }
    /**
     * Pages of OTHER modules Reseller's tables link to, each with a `:id`
     * placeholder. They used to be `pathKey`s into the app-wide `paths`, which
     * no import line shows.
     */
    links: HostLinks<'customerDetail' | 'serviceDetail' | 'invoiceDetail'>
    /** The app's server-searched customer select. */
    CustomerSelect: ComponentType<ResellerCustomerSelectProps>
}

let current: ResellerHost | undefined

export function registerResellerHost(host: ResellerHost): void {
    current = host
    host.i18n.addResourceBundle('vi', 'reseller', vi)
    host.i18n.addResourceBundle('en', 'reseller', en)
}

/** The registered host. Read it at render or call time, never at import. */
export function getResellerHost(): ResellerHost {
    if (!current)
        throw new Error(
            '[reseller] no host registered. Call createResellerModule({ i18n, links, … }) at app bootstrap (src/app/register-reseller.tsx).'
        )
    return current
}
